import { useState, useEffect, type FC } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import {
  Code as APIIcon,
  Api as GraphQLIcon,
  Http as RestIcon,
  Settings as GrpcIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { APISelectionCardProps } from './types';
import { useAPIData } from '../../hooks/useAPIData';
import { useRepositoryData } from '../../hooks/useRepositoryData';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}10 50%, ${theme.palette.background.paper} 100%)`,
  border: `1px solid ${theme.palette.primary.light}40`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.secondary.light}15 50%, ${theme.palette.background.paper} 100%)`,
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 400,
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.action.hover,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: 4,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.002)',
  },
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.primary.light}20`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.light}30`,
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontSize: '0.9rem',
  fontWeight: 500,
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 1.5),
  fontSize: '0.85rem',
  fontWeight: 700,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  position: 'sticky',
  top: 0,
  zIndex: 3,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TypeChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 24,
  fontWeight: 600,
  '&.openapi': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  '&.graphql': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  '&.grpc': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  '&.default': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const getTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'openapi':
    case 'rest':
      return <RestIcon />;
    case 'graphql':
      return <GraphQLIcon />;
    case 'grpc':
      return <GrpcIcon />;
    default:
      return <APIIcon />;
  }
};

const getTypeChipClass = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'openapi':
    case 'rest':
      return 'openapi';
    case 'graphql':
      return 'graphql';
    case 'grpc':
      return 'grpc';
    default:
      return 'default';
  }
};

export const APISelectionCard: FC<APISelectionCardProps> = ({
  selectedRepository,
  selectedApis,
  onApiChange,
}) => {
  const { repositories } = useRepositoryData();
  const [selectAll, setSelectAll] = useState(false);

  // Get the selected repository's system
  const selectedRepo = repositories.find(
    repo => repo.value === selectedRepository,
  );
  const selectedRepositorySystem = selectedRepo?.system;

  const { apis, loading, error } = useAPIData(selectedRepositorySystem);

  // Calculate selection state
  const isSelected = (entityRef: string) => selectedApis.includes(entityRef);
  const selectedCount = selectedApis.length;
  const totalCount = apis.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  // Update select all state when selections change
  useEffect(() => {
    setSelectAll(selectedCount > 0 && selectedCount === totalCount);
  }, [selectedCount, totalCount]);

  // Handle individual item selection
  const handleToggle = (entityRef: string) => {
    const newSelected = isSelected(entityRef)
      ? selectedApis.filter(ref => ref !== entityRef)
      : [...selectedApis, entityRef];
    onApiChange(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll || isIndeterminate) {
      onApiChange([]);
    } else {
      onApiChange(apis.map(api => api.entityRef));
    }
  };

  // Get repository system for empty state message
  const systemName = selectedRepo?.system || 'system';

  // Don't render if no repository is selected
  if (!selectedRepository) {
    return null;
  }

  return (
    <Fade in timeout={300}>
      <StyledCard>
        <CardContent>
          <CardHeader>
            <APIIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                APIs Provided by this Component
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Select APIs that this component provides
              </Typography>
            </Box>
            {totalCount > 0 && (
              <Chip
                label={`${selectedCount} of ${totalCount} selected`}
                color="primary"
                size="small"
                variant="outlined"
              />
            )}
          </CardHeader>

          {loading && (
            <Box>
              {['first', 'second', 'third'].map(id => (
                <Skeleton
                  key={`api-skeleton-${id}`}
                  variant="rectangular"
                  height={56}
                  sx={{ mb: 1, borderRadius: 1 }}
                />
              ))}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && apis.length === 0 && (
            <EmptyStateBox>
              <APIIcon sx={{ fontSize: 48, mb: 2, opacity: 0.6 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                No API provided by {systemName} found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No APIs are available for the selected repository's system.
              </Typography>
            </EmptyStateBox>
          )}

          {!loading && !error && apis.length > 0 && (
            <StyledTableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeaderCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={selectAll}
                        onChange={handleSelectAll}
                        color="primary"
                      />
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                    <StyledTableHeaderCell>System</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Description</StyledTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apis.map(api => (
                    <StyledTableRow
                      key={api.entityRef}
                      hover
                      onClick={() => handleToggle(api.entityRef)}
                      selected={isSelected(api.entityRef)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected(api.entityRef)}
                          onChange={event => {
                            event.stopPropagation();
                            handleToggle(api.entityRef);
                          }}
                          color="primary"
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {getTypeIcon(api.type)}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {api.name}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <TypeChip
                          label={api.type}
                          size="small"
                          className={getTypeChipClass(api.type)}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2">{api.system}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={api.description}
                        >
                          {api.description}
                        </Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}

          {!loading && !error && apis.length > 0 && selectedCount > 0 && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {selectedCount} of {totalCount} APIs selected
              </Typography>
            </Box>
          )}
        </CardContent>
      </StyledCard>
    </Fade>
  );
};
